import { Component } from '@theme/component';
import { bindOverflowCarousel } from '@theme/bt-scroll-carousel';

/**
 * Bo+Tee Featured Collection Carousel — tabs and CTA on top of
 * `@theme/bt-scroll-carousel` for progress and desktop prev/next.
 *
 * @extends {Component}
 */
class BtFeaturedCollectionCarousel extends Component {
  /** @type {HTMLElement | null} */
  #tablist = null;

  /** @type {import('./bt-scroll-carousel').OverflowCarouselBinding | null} */
  #carousel = null;

  connectedCallback() {
    super.connectedCallback();
    this.#tablist = this.querySelector('[role="tablist"]');
    this.#hydrateTabsFromPanels();
    this.#carousel = bindOverflowCarousel({
      root: this,
      track: () => this.#activeTrack(),
      prevButton: this.querySelector('[data-bt-fcc-prev]'),
      nextButton: this.querySelector('[data-bt-fcc-next]'),
      cardSelector: '.bt-fcc__card',
      progressVar: '--bt-fcc-progress',
      features: { progress: true, nav: true },
    });
    this.addEventListener('click', this.#onClick);
    this.#tablist?.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#tablist?.removeEventListener('keydown', this.#onKeydown);
    this.#carousel?.disconnect();
    this.#carousel = null;
    this.#tablist = null;
  }

  #hydrateTabsFromPanels() {
    if (!this.#tablist) return;

    this.#tablist.replaceChildren();

    const panels = this.querySelectorAll('.bt-fcc__panel');
    let firstButton = null;
    let index = 0;

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      const label = panel.dataset.tabLabel?.trim();
      const tabId = panel.dataset.tabId;
      if (!label || !tabId) continue;

      const isActive = panel.classList.contains('is-active') || (!firstButton && index === 0);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `bt-fcc__tab ${isActive ? 'text-p4-bold color-text-primary is-active' : 'text-p4 color-text-subdued'}`;
      button.id = `BtFcc-tab-${this.dataset.sectionId}-${tabId}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('aria-controls', panel.id);
      button.tabIndex = isActive ? 0 : -1;
      button.dataset.tabId = tabId;
      button.dataset.link = panel.dataset.tabLink || '';
      button.textContent = label;
      this.#tablist.appendChild(button);

      if (isActive) {
        firstButton = button;
        panel.classList.add('is-active');
        panel.hidden = false;
      } else {
        panel.classList.remove('is-active');
        panel.hidden = true;
      }
      index += 1;
    }

    if (firstButton) {
      this.#updateCta(firstButton.dataset.link || '');
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #activeTrack() {
    const panel = this.querySelector('.bt-fcc__panel.is-active, .bt-fcc__panel:not([hidden])');
    if (panel instanceof HTMLElement) {
      const track = panel.querySelector('[data-bt-fcc-track]');
      if (track instanceof HTMLElement) return track;
    }
    const fallback = this.querySelector('[data-bt-fcc-track]');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;
    this.#activate(tab, { focus: false });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const tabs = this.#tabs();
    if (!tabs.length) return;

    const current = document.activeElement;
    if (!(current instanceof HTMLElement) || current.getAttribute('role') !== 'tab') return;

    const index = tabs.indexOf(current);
    if (index < 0) return;

    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(tabs[nextIndex], { focus: true });
  };

  /**
   * @returns {HTMLElement[]}
   */
  #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]')).filter((el) => el instanceof HTMLElement);
  }

  /**
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  #activate(tab, options = {}) {
    const tabId = tab.dataset.tabId;
    if (!tabId) return;

    for (const other of this.#tabs()) {
      const selected = other === tab;
      other.classList.toggle('is-active', selected);
      other.classList.toggle('text-p4-bold', selected);
      other.classList.toggle('color-text-primary', selected);
      other.classList.toggle('text-p4', !selected);
      other.classList.toggle('color-text-subdued', !selected);
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.querySelectorAll('.bt-fcc__panel')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.tabId === tabId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    }

    this.#updateCta(tab.dataset.link || '');
    this.#carousel?.update();

    if (options.focus) {
      tab.focus();
    }
  }

  /**
   * @param {string} link
   */
  #updateCta(link) {
    if (this.hasAttribute('data-bt-fcc-cta-locked')) return;

    const href = link.trim();
    if (!href) return;

    for (const cta of this.querySelectorAll('[data-bt-fcc-cta]')) {
      if (!(cta instanceof HTMLAnchorElement)) continue;
      cta.href = href;
    }
  }
}

if (!customElements.get('bt-featured-collection-carousel')) {
  customElements.define('bt-featured-collection-carousel', BtFeaturedCollectionCarousel);
}
